import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { signIn, confirmSignIn, getCurrentUser, signOut, rememberDevice, forgetDevice } from "aws-amplify/auth";
import { ShieldCheck, KeyRound, Copy, Check, ArrowLeft, Lock } from "lucide-react";
import { API_BASE_URL } from "../config";
import { generateQrMatrix } from "../lib/qrcode";

type LoginStep = "CREDENTIALS" | "TOTP_SETUP" | "TOTP_CODE" | "NEW_PASSWORD";

export const REMEMBER_DEVICE_STORAGE_KEY = "afima_remember_device";

async function logAuthAttempt(payload: {
  event_type: "login_failed" | "mfa_failed" | "unauthorized_user" | "login_success" | "mfa_enrolled";
  email: string;
  error_code?: string;
  error_message?: string;
  step: string;
}) {
  try {
    await fetch(`${API_BASE_URL}/api/auth/log-attempt`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...payload,
        origin: window.location.origin,
        path: window.location.pathname,
      }),
    });
  } catch {
    // Never block UI if audit endpoint is unreachable
  }
}

export default function Login() {
  const navigate = useNavigate();
  const [step, setStep] = useState<LoginStep>("CREDENTIALS");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [totpSecret, setTotpSecret] = useState("");
  const [totpUri, setTotpUri] = useState("");
  const [rememberThisDevice, setRememberThisDevice] = useState(true);
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    document.documentElement.classList.add("h-full", "bg-white");
    document.body.classList.add("h-full");

    const checkUser = async () => {
      try {
        const user = await getCurrentUser();
        if (user) navigate("/");
      } catch {
        // not signed in
      }
    };

    checkUser();

    return () => {
      document.documentElement.classList.remove("h-full", "bg-white");
      document.body.classList.remove("h-full");
    };
  }, [navigate]);

  const qrMatrix = useMemo(() => {
    if (!totpUri) return null;
    try {
      return generateQrMatrix(totpUri);
    } catch (e) {
      console.error("Failed to generate QR matrix:", e);
      return null;
    }
  }, [totpUri]);

  const handleNextSignInStep = async (nextStep: any, normalizedEmail: string) => {
    const stepType = nextStep?.signInStep;

    if (!stepType || stepType === "DONE") {
      try {
        localStorage.setItem(REMEMBER_DEVICE_STORAGE_KEY, "true");
      } catch {
        // ignore storage errors
      }
      await logAuthAttempt({
        event_type: "login_success",
        email: normalizedEmail,
        step: "COMPLETE",
      });
      navigate("/");
      return;
    }

    if (stepType === "SELECT_MFA_TYPE") {
      const selRes = await confirmSignIn({ challengeResponse: "TOTP" });
      await handleNextSignInStep(selRes.nextStep, normalizedEmail);
      return;
    }

    if (stepType === "CONTINUE_SIGN_IN_WITH_TOTP_SETUP") {
      const details = nextStep.totpSetupDetails;
      const secret = details?.sharedSecret || "";
      let uri = "";
      try {
        const uriObj = details?.getSetupUri?.("Afima.ai", normalizedEmail);
        uri = uriObj ? uriObj.toString() : "";
      } catch {
        uri = secret
          ? `otpauth://totp/Afima.ai:${encodeURIComponent(normalizedEmail)}?secret=${secret}&issuer=Afima.ai`
          : "";
      }
      setTotpSecret(secret);
      setTotpUri(uri);
      setTotpCode("");
      setStep("TOTP_SETUP");
      return;
    }

    if (
      stepType === "CONFIRM_SIGN_IN_WITH_TOTP_CODE" ||
      stepType === "CONFIRM_SIGN_IN_WITH_SMS_CODE" ||
      stepType === "CONFIRM_SIGN_IN_WITH_EMAIL_CODE"
    ) {
      setTotpCode("");
      setStep("TOTP_CODE");
      return;
    }

    if (stepType === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
      setNewPassword("");
      setConfirmPassword("");
      setStep("NEW_PASSWORD");
      return;
    }

    setError(`Unsupported authentication step: ${stepType}`);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const normalizedEmail = email.trim().toLowerCase();

    try {
      // Clear any stale partial auth session before starting a fresh sign-in
      try {
        await signOut();
      } catch {
        // ignore
      }

      const result = await signIn({ username: normalizedEmail, password });
      await handleNextSignInStep(result.nextStep, normalizedEmail);
    } catch (err: any) {
      console.error(err);
      const errName = err?.name || "AuthError";
      const errMsg = err?.message || "Login failed";
      await logAuthAttempt({
        event_type: "login_failed",
        email: normalizedEmail,
        error_code: errName,
        error_message: errMsg,
        step: "PASSWORD",
      });
      setError(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmTotp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    const cleanCode = totpCode.replace(/\s+/g, "");
    if (!/^\d{6}$/.test(cleanCode)) {
      setError("Please enter a valid 6-digit authenticator code.");
      return;
    }

    setSubmitting(true);
    const normalizedEmail = email.trim().toLowerCase();
    const currentStepLabel = step === "TOTP_SETUP" ? "TOTP_SETUP" : "TOTP_MFA";

    try {
      const result = await confirmSignIn({ challengeResponse: cleanCode });
      if (result.isSignedIn || result.nextStep?.signInStep === "DONE") {
        try {
          localStorage.setItem(REMEMBER_DEVICE_STORAGE_KEY, rememberThisDevice ? "true" : "false");
          if (rememberThisDevice) {
            await rememberDevice();
          } else {
            await forgetDevice();
          }
        } catch (deviceErr) {
          console.warn("Device remember update failed:", deviceErr);
        }
        await logAuthAttempt({
          event_type: step === "TOTP_SETUP" ? "mfa_enrolled" : "login_success",
          email: normalizedEmail,
          step: currentStepLabel,
        });
        navigate("/");
      } else {
        await handleNextSignInStep(result.nextStep, normalizedEmail);
      }
    } catch (err: any) {
      console.error(err);
      const errName = err?.name || "MfaError";
      const errMsg = err?.message || "Invalid verification code";
      await logAuthAttempt({
        event_type: "mfa_failed",
        email: normalizedEmail,
        error_code: errName,
        error_message: errMsg,
        step: currentStepLabel,
      });
      setError(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (newPassword.length < 12) {
      setError("New password must be at least 12 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setSubmitting(true);
    const normalizedEmail = email.trim().toLowerCase();
    try {
      const result = await confirmSignIn({ challengeResponse: newPassword });
      await handleNextSignInStep(result.nextStep, normalizedEmail);
    } catch (err: any) {
      console.error(err);
      const errName = err?.name || "NewPasswordError";
      const errMsg = err?.message || "Failed to set new password";
      await logAuthAttempt({
        event_type: "login_failed",
        email: normalizedEmail,
        error_code: errName,
        error_message: errMsg,
        step: "NEW_PASSWORD",
      });
      setError(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const copySecretKey = async () => {
    if (!totpSecret) return;
    try {
      await navigator.clipboard.writeText(totpSecret);
      setCopiedSecret(true);
      setTimeout(() => setCopiedSecret(false), 2000);
    } catch {
      // fallback
    }
  };

  const resetToCredentials = () => {
    setStep("CREDENTIALS");
    setTotpCode("");
    setTotpSecret("");
    setTotpUri("");
    setError("");
  };

  return (
    <div className="flex min-h-full flex-col justify-center px-6 py-12 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <img
          className="mx-auto h-10 w-auto"
          src="/logo192.png"
          alt="Afima"
        />
        <h2 className="mt-8 text-center text-2xl font-bold tracking-tight text-gray-900">
          {step === "CREDENTIALS" && "Sign in to your account"}
          {step === "TOTP_SETUP" && "Set up Two-Factor Authentication"}
          {step === "TOTP_CODE" && "Two-Factor Verification"}
          {step === "NEW_PASSWORD" && "Set a new password"}
        </h2>
        {step === "TOTP_SETUP" && (
          <p className="mt-2 text-center text-sm text-gray-600">
            Scan the QR code below with your authenticator app (Google Authenticator, 1Password, Apple Passwords, or Authy), then enter the 6-digit code.
          </p>
        )}
        {step === "TOTP_CODE" && (
          <p className="mt-2 text-center text-sm text-gray-600">
            Enter the 6-digit verification code from your authenticator app for <span className="font-medium text-gray-900">{email}</span>.
          </p>
        )}
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white px-6 py-8 shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl sm:px-8">
          {step === "CREDENTIALS" && (
            <form className="space-y-6" onSubmit={handleLogin}>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-900">
                  Email address
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="username"
                  required
                  className="mt-2 block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline outline-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:outline-indigo-600 sm:text-sm"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-900">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  className="mt-2 block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline outline-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:outline-indigo-600 sm:text-sm"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>

              {error && (
                <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 border border-red-200">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full flex justify-center items-center gap-2 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-500 focus:outline-indigo-600 disabled:opacity-60"
              >
                <Lock className="h-4 w-4" />
                {submitting ? "Signing in..." : "Sign in"}
              </button>
            </form>
          )}

          {step === "TOTP_SETUP" && (
            <form className="space-y-6" onSubmit={handleConfirmTotp}>
              {qrMatrix && (
                <div className="flex flex-col items-center justify-center p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <svg
                    viewBox={`0 0 ${qrMatrix.length + 8} ${qrMatrix.length + 8}`}
                    className="w-48 h-48 bg-white p-2 rounded shadow-xs border border-gray-100"
                    shapeRendering="crispEdges"
                    aria-label="Authenticator QR Code"
                  >
                    <rect width="100%" height="100%" fill="#ffffff" />
                    {qrMatrix.map((row, rIdx) =>
                      row.map((cell, cIdx) =>
                        cell ? (
                          <rect
                            key={`${rIdx}-${cIdx}`}
                            x={cIdx + 4}
                            y={rIdx + 4}
                            width={1}
                            height={1}
                            fill="#111827"
                          />
                        ) : null
                      )
                    )}
                  </svg>
                </div>
              )}

              {totpSecret && (
                <div className="rounded-md bg-gray-50 p-3 border border-gray-200">
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                    <span className="font-medium flex items-center gap-1">
                      <KeyRound className="h-3.5 w-3.5" />
                      Setup key (if you can&apos;t scan the QR code)
                    </span>
                    <button
                      type="button"
                      onClick={copySecretKey}
                      className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-500 font-medium"
                    >
                      {copiedSecret ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                      {copiedSecret ? "Copied" : "Copy"}
                    </button>
                  </div>
                  <div className="font-mono text-xs text-gray-800 break-all select-all bg-white px-2.5 py-1.5 rounded border border-gray-200">
                    {totpSecret}
                  </div>
                </div>
              )}

              <div>
                <label htmlFor="totp-setup-code" className="block text-sm font-medium text-gray-900">
                  6-digit verification code
                </label>
                <input
                  id="totp-setup-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="000000"
                  required
                  autoFocus
                  className="mt-2 block w-full rounded-md bg-white px-3 py-2 text-center font-mono text-lg tracking-widest text-gray-900 outline outline-1 outline-gray-300 placeholder:text-gray-300 focus:outline-2 focus:outline-indigo-600"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                />
              </div>

              <label className="flex items-start gap-2.5 cursor-pointer select-none rounded-md bg-gray-50 px-3 py-2.5 border border-gray-200">
                <input
                  type="checkbox"
                  checked={rememberThisDevice}
                  onChange={(e) => setRememberThisDevice(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                />
                <span className="text-xs text-gray-700 leading-relaxed">
                  <span className="font-semibold text-gray-900">Remember this device</span> — stay signed in for 30 days and skip 2FA on this browser.
                </span>
              </label>

              {error && (
                <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 border border-red-200">
                  {error}
                </div>
              )}

              <div className="space-y-3">
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full flex justify-center items-center gap-2 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-500 focus:outline-indigo-600 disabled:opacity-60"
                >
                  <ShieldCheck className="h-4 w-4" />
                  {submitting ? "Verifying..." : "Verify & Enable 2FA"}
                </button>
                <button
                  type="button"
                  onClick={resetToCredentials}
                  className="w-full flex justify-center items-center gap-1.5 rounded-md bg-white px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 border border-gray-200"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Back to sign in
                </button>
              </div>
            </form>
          )}

          {step === "TOTP_CODE" && (
            <form className="space-y-6" onSubmit={handleConfirmTotp}>
              <div>
                <label htmlFor="totp-code" className="block text-sm font-medium text-gray-900">
                  Authenticator code
                </label>
                <input
                  id="totp-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="000000"
                  required
                  autoFocus
                  className="mt-2 block w-full rounded-md bg-white px-3 py-2.5 text-center font-mono text-xl tracking-widest text-gray-900 outline outline-1 outline-gray-300 placeholder:text-gray-300 focus:outline-2 focus:outline-indigo-600"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                />
              </div>

              <label className="flex items-start gap-2.5 cursor-pointer select-none rounded-md bg-gray-50 px-3 py-2.5 border border-gray-200">
                <input
                  type="checkbox"
                  checked={rememberThisDevice}
                  onChange={(e) => setRememberThisDevice(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                />
                <span className="text-xs text-gray-700 leading-relaxed">
                  <span className="font-semibold text-gray-900">Remember this device</span> — stay signed in for 30 days and skip 2FA on this browser.
                </span>
              </label>

              {error && (
                <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 border border-red-200">
                  {error}
                </div>
              )}

              <div className="space-y-3">
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full flex justify-center items-center gap-2 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-500 focus:outline-indigo-600 disabled:opacity-60"
                >
                  <ShieldCheck className="h-4 w-4" />
                  {submitting ? "Verifying..." : "Verify & Sign in"}
                </button>
                <button
                  type="button"
                  onClick={resetToCredentials}
                  className="w-full flex justify-center items-center gap-1.5 rounded-md bg-white px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 border border-gray-200"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Back to sign in
                </button>
              </div>
            </form>
          )}

          {step === "NEW_PASSWORD" && (
            <form className="space-y-6" onSubmit={handleConfirmNewPassword}>
              <div>
                <label htmlFor="new-password" className="block text-sm font-medium text-gray-900">
                  New password (min. 12 characters)
                </label>
                <input
                  id="new-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  className="mt-2 block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline outline-1 outline-gray-300 focus:outline-2 focus:outline-indigo-600 sm:text-sm"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-900">
                  Confirm new password
                </label>
                <input
                  id="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  className="mt-2 block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline outline-1 outline-gray-300 focus:outline-2 focus:outline-indigo-600 sm:text-sm"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>

              {error && (
                <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 border border-red-200">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-500 focus:outline-indigo-600 disabled:opacity-60"
              >
                {submitting ? "Saving..." : "Update password & continue"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
